import { useEffect, useState } from "react";

import Box from "@/components/Box";
import { useModalWorkFlow } from "@/contexts/ModalWorkFlowProvider";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import Checkbox from "@/tpo/Checkbox";
import Modal from "@/tpo/Modal";

import {
  getMissingFieldsForPatient,
  getSavePatientEditableFields
} from "../../shared/PatientDetails";
import SelectPatient from "../../shared/ui/SelectPatient";
import SavePatientForm from "../forms/SavePatientForm";
import useDynamicCmpPermission, { DYNAMIC_CMP_PERMISSION_QUERY } from "@/hooks/useDynamicCmpPermission";
import { ORGANISATION_LIST_QUERY } from "@/graphql/organisations/queries/organisation";

export default function SelectPatientPerLine({ patient: patientProfile, basketLineItemId }) {
  const { organisation } = useOrganisationContext();
  const { setPatientOnBasketLine, updateBasketDetails, basket, loading } =
    useOrganisationBasketContext();
  const patient = organisation?.patients.find(
    p => p.patientProfile.pk === patientProfile?.pk
  );

  const [showCreatePatientModal, setShowCreatePatientModal] = useState(false);
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);

  const modalWorkFlowApi = useModalWorkFlow();

  const [value, setValue] = useState(patient?.id || "");

  useEffect(() => {
    if (value === "" && patient?.id) {
      setValue(patient.id);
    } else if (value && patient?.id && patient.id !== value) {
      setValue(patient.id);
    } else if (value !== "" && patient?.id === undefined) {
      setValue("");
    }
  }, [patient, value]);


  const canCreatePatients = useDynamicCmpPermission({
    permission: "can_create_patient"
  });

  return (
    <Box bg="haze" p={[20, 20, 40]}>
      <SelectPatient
        canCreate={canCreatePatients}
        selectedPatient={patient}
        onSelect={v => {
          let pup = organisation.patients.find(p => p.id === v)?.patientProfile;
          const patientValue = pup?.pk || "";
          if (
            (patientProfile && patientProfile.pk !== patientValue) ||
            (patientProfile === null && patientValue !== "")
          ) {
            setPatientOnBasketLine({
              id: basketLineItemId,
              patient: patientValue
            });
          }
        }}
        title="Assign this item to a patient"
        titleProps={{
          fontSize: [16, 16, 18]
        }}
        descriptionProps={{
          fontSize: [14],
          mt: 2
        }}
        buttonProps={{
          mt: 16,
          size: "xs",
          disabled: loading
        }}
        selectPatientFieldProps={{
          mt: 30
        }}
        patientDetailsProps={{
          mt: 30
        }}
        value={value}
        setValue={setValue}
        onCreate={() => {
          if (modalWorkFlowApi) {
            modalWorkFlowApi.setView("createPatient", {
              creatingForLineItem: basketLineItemId
            });
          } else {
            setShowCreatePatientModal(true);
          }
        }}
        onEdit={() => {
          if (modalWorkFlowApi) {
            modalWorkFlowApi.setView("editPatient", {
              organisationPatientId: patient.id
            });
          } else {
            setShowEditPatientModal(true);
          }
        }}
      />
      <Checkbox
        disabled={!patientProfile || loading}
        checked={basket?.patient && basket?.patient?.pk === patientProfile?.pk}
        onChange={e => {
          updateBasketDetails({
            patient: e.target.checked ? patientProfile.pk : "",
            usePatientAddress: false,
            shippingAddress: basket?.shippingAddress?.id
          });
        }}
        size="sm"
        label="Set this patient as the primary contact for billing"
        styles={{
          wrapperProps: {
            mt: 20
          },
          labelProps: {
            fontSize: 14
          }
        }}
      />
      <Modal
        maxWidth={1020}
        closeButton
        headerProps={{
          p: [20, 20, 40]
        }}
        bg="white"
        show={showCreatePatientModal}
        close={() => setShowCreatePatientModal(false)}
        mode={["fullScreen", "fullScreen", "centered"]}
      >
        <Box px={[20]} maxWidth={760} mx="auto">
          <SavePatientForm
            key="create"
            handleSuccess={({ data }) => {
              setValue("");
              setPatientOnBasketLine({
                id: basketLineItemId,
                patient: data.savePatientMutation.patient.patientProfile.pk
              });
              setShowCreatePatientModal(false);
            }}
            mutationOptions={{
              refetchQueries: [
                { query: ORGANISATION_LIST_QUERY },
                { query: DYNAMIC_CMP_PERMISSION_QUERY, variables: { organisationId: organisation.id, permission: "can_create_patient" } }
              ],
            }}
          />
        </Box>
      </Modal>
      <Modal
        maxWidth={1020}
        closeButton
        headerProps={{
          p: [20, 20, 40]
        }}
        bg="white"
        show={showEditPatientModal}
        close={() => setShowEditPatientModal(false)}
        mode={["fullScreen", "fullScreen", "centered"]}
        data-component-name="EditPatientModal"
      >
        <Box px={[20]} maxWidth={760} mx="auto">
          {patient && (
            <SavePatientForm
              key="edit"
              title="Edit Patient Details"
              data-component-name="EditPatientForm"
              editableFields={getSavePatientEditableFields(patient)}
              missingFields={getMissingFieldsForPatient(patient)}
              defaultValues={{
                id: patient.id,
                firstName: patient.patientProfile.user.firstName,
                lastName: patient.patientProfile.user.lastName,
                email: patient.patientProfile.user.email,
                sex: patient.patientProfile.gender,
                dateOfBirth: patient.patientProfile.dateOfBirth || "",
                requiresGuardianConsent: patient.requiresGuardianConsent,
                guardianFirstName: patient.patientProfile.guardian?.user.firstName || "",
                guardianLastName: patient.patientProfile.guardian?.user.lastName || "",
                guardianEmail: patient.patientProfile.guardian?.user.email || ""
              }}
              handleSuccess={({ data }) => {
                setShowEditPatientModal(false);
              }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
}
